import { useState, useEffect, useMemo } from "react";
import axiosInstance from "../../../../Config/axiosInstance";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import "./ProjectDelete.css";

export default function ProjectCodeDeleteDialog({
  open,
  onClose,
  record,
  onDeleted,
}) {
  /* State pentru a controla loading-ul de ștergere */
  const [deleting, setDeleting] = useState(false);

  /* State pentru mesajul de eroare afișat în dialog */
  const [error, setError] = useState("");

  const rawRecord = useMemo(() => record?.__raw ?? record ?? null, [record]);

  /* La deschiderea dialogului resetăm starea:*/
  useEffect(() => {
    if (!open) return;
    setDeleting(false);
    setError("");
  }, [open]);

  /* Funcția care face efectiv ștergerea.*/
  const handleDelete = async () => {
    if (!rawRecord?.id) return;

    setError("");
    setDeleting(true);

    try {
      await axiosInstance.delete(`/code_registries/${rawRecord.id}/`);
      onDeleted?.();
    } catch (e) {
      setError(
        "Cannot delete this project code because it has dependencies in other records. Please remove or update those dependencies first."
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={deleting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      className="subproject-delete-dialog"
      PaperProps={{
        elevation: 24,
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {!error && (
        <DialogTitle className="spd-header">
          <Box className="spd-header-content">
            <Box className="spd-title-section">
              <WarningAmberIcon className="spd-warning-icon" />
              <Typography variant="h5" component="span" className="spd-title">
                Delete Project Code
              </Typography>
            </Box>

            <IconButton
              onClick={onClose}
              className="spd-close-btn"
              size="small"
              disabled={deleting}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
      )}

      <DialogContent className="spd-content">
        {error ? (
          <Alert
            severity="error"
            className="spd-error-alert"
            icon={<ErrorOutlineIcon />}
          >
            <Typography variant="body1" fontWeight={500}>
              Unable to Delete Project Code
            </Typography>
            <Typography variant="body2" className="spd-error-message">
              {error}
            </Typography>
          </Alert>
        ) : (
          <Box className="spd-confirm-content">
            {/* Dacă există cod, îl afișăm ca să fie clar ce se șterge */}
            {rawRecord?.code && (
              <Box className="spd-code-container">
                <Typography variant="subtitle2" className="spd-code-label">
                  Project Code
                </Typography>
                <Typography variant="h6" className="spd-code-value">
                  {rawRecord.code}
                </Typography>
              </Box>
            )}

            <Typography variant="body1" className="spd-warning-text">
              This action is permanent and cannot be undone.
            </Typography>

            <Box className="spd-consequences">
              <Typography variant="body2" className="spd-consequence-item">
                • All associated data will be permanently deleted
              </Typography>
              <Typography variant="body2" className="spd-consequence-item">
                • Related configurations will be removed
              </Typography>
              <Typography variant="body2" className="spd-consequence-item">
                • This change cannot be reversed
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions className="spd-actions">
        {error ? (
          <Button
            onClick={onClose}
            variant="contained"
            className="spd-btn spd-btn-ok"
          >
            Got It
          </Button>
        ) : (
          <>
            {/* Butonul Cancel */}
            <Button
              onClick={onClose}
              disabled={deleting}
              variant="outlined"
              className="spd-btn spd-btn-cancel"
            >
              Cancel
            </Button>

            {/* Butonul Delete */}
            <Button
              onClick={handleDelete}
              disabled={deleting || !rawRecord?.id}
              variant="contained"
              className="spd-btn spd-btn-delete"
              startIcon={
                deleting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <DeleteOutlineIcon />
                )
              }
              autoFocus
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}